import React from 'react';
import { startCase } from 'lodash';
import { Box } from '@mui/material';

import {
  EmptyDataTable,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@occc-app/design-system';
import { readableDate } from '@occc-app/lib/format';
import { MembershipStateChangeFragment, MembershipStateChangeSourceFragment } from '@occc/graphql';
import { TableField, Row } from '@occc/ui-design-system';

type StateChangesProps = {
  stateChanges: MembershipStateChangeFragment[];
};

type StateChangeRow = Row[] | null | undefined;

const headers = [{ label: 'Date' }, { label: 'State' }, { label: 'Source' }];

const formatValue = (value: string | null | undefined): string =>
  value ? startCase(value.toLowerCase()) : '';

const formatSource = (source: MembershipStateChangeSourceFragment | null | undefined): string => {
  if (source?.__typename === 'ActivateTransaction') {
    return `Transaction ID: ${source.id}`;
  }

  if (source?.__typename === 'Membership') {
    return 'Membership event';
  }

  if (source?.__typename === 'CommandCenterUser') {
    return `User: ${source.name || source.email}`;
  }

  return '';
};

const makeFields: (stateChange: MembershipStateChangeFragment) => TableField[] = ({
  state,
  effectiveAt,
  source,
}) => [
  {
    label: 'Date',
    value: readableDate(effectiveAt),
  },
  {
    label: 'State',
    value: formatValue(state),
  },
  {
    label: 'Source',
    value: formatSource(source),
  },
];

const StateChangesTable = ({ stateChanges }: StateChangesProps) => {
  const rows: StateChangeRow = stateChanges.map((stateChange, index) => ({
    key: `${index}-${stateChange.state}`,
    fields: makeFields(stateChange),
  }));

  if (!stateChanges) {
    return (
      <Box p={4}>
        <Typography color='error' data-testid='no-data-msg'>
          No data. Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (stateChanges.length === 0) {
    return <EmptyDataTable />;
  }

  const tableRows = rows?.map((row) => {
    return (
      <TableRow key={row.key}>
        {row.fields.map(({ label, value }) => (
          <TableCell key={label}>{value}</TableCell>
        ))}
      </TableRow>
    );
  });

  return (
    <Table>
      <TableHead>
        <TableRow>
          {headers.map(({ label }) => (
            <TableCell key={label}>{label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>{tableRows}</TableBody>
    </Table>
  );
};

export default StateChangesTable;
